/**
 * SystemRecoveryPanel - Platform-level backup, restore, and maintenance controls
 * 
 * This component provides:
 * - Maintenance Mode toggle with confirmation
 * - Backup health status indicator
 * - Create backup action
 * - Collapsible backup list with restore capability
 * 
 * All actions require platform_admin role and typed confirmations for safety.
 */

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../components/ui/collapsible";
import { toast } from "sonner";
import {
  ShieldCheck,
  Database,
  CheckCircle,
  Warning,
  X,
  CaretDown,
  CaretRight,
  Clock,
  ArrowCounterClockwise,
  Plus,
  Trash,
  HardDrive,
  Wrench,
  SpinnerGap,
} from "phosphor-react";
import { format, formatDistanceToNow } from "date-fns";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function SystemRecoveryPanel() {
  // State
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [backups, setBackups] = useState([]);
  const [backupsOpen, setBackupsOpen] = useState(false);
  
  // Action states
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [restoringBackup, setRestoringBackup] = useState(null);
  const [togglingMaintenance, setTogglingMaintenance] = useState(false);
  
  // Modal states
  const [maintenanceModalOpen, setMaintenanceModalOpen] = useState(false);
  const [maintenanceAction, setMaintenanceAction] = useState(null); // 'enable' or 'disable'
  const [maintenanceConfirm, setMaintenanceConfirm] = useState("");
  const [maintenanceMessage, setMaintenanceMessage] = useState("");
  
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [restoreConfirm, setRestoreConfirm] = useState("");

  // Fetch status
  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/system-recovery/status`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (error) {
      console.error("Failed to fetch recovery status:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch backups
  const fetchBackups = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/system-recovery/backups`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setBackups(data.backups || []);
      }
    } catch (error) {
      console.error("Failed to fetch backups:", error);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchBackups();
  }, [fetchStatus, fetchBackups]);

  // Create backup
  const handleCreateBackup = async () => {
    setCreatingBackup(true);
    try {
      const response = await fetch(`${API}/admin/system-recovery/backups`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ description: "Manual backup from admin panel" }),
      });
      
      if (response.ok) {
        toast.success("Backup created successfully");
        fetchBackups();
        fetchStatus();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to create backup");
      }
    } catch (error) {
      toast.error("Failed to create backup");
    } finally {
      setCreatingBackup(false);
    }
  };

  // Toggle maintenance mode
  const handleMaintenanceToggle = async () => {
    if (maintenanceConfirm !== "MAINTENANCE") {
      toast.error("Please type MAINTENANCE to confirm");
      return;
    }
    
    setTogglingMaintenance(true);
    try {
      const response = await fetch(`${API}/admin/system-recovery/maintenance`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          enabled: maintenanceAction === "enable",
          confirm: "MAINTENANCE",
          message: maintenanceMessage || null,
        }),
      });
      
      if (response.ok) {
        toast.success(`Maintenance mode ${maintenanceAction === "enable" ? "enabled" : "disabled"}`);
        setMaintenanceModalOpen(false);
        setMaintenanceConfirm("");
        setMaintenanceMessage("");
        fetchStatus();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to toggle maintenance mode");
      }
    } catch (error) {
      toast.error("Failed to toggle maintenance mode");
    } finally {
      setTogglingMaintenance(false);
    }
  };

  // Restore backup
  const handleRestore = async () => {
    if (restoreConfirm !== "RESTORE") {
      toast.error("Please type RESTORE to confirm");
      return;
    }
    
    if (!selectedBackup) return;
    
    setRestoringBackup(selectedBackup.backup_id);
    try {
      const response = await fetch(
        `${API}/admin/system-recovery/backups/${selectedBackup.backup_id}/restore`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ confirm: "RESTORE" }),
        }
      );
      
      if (response.ok) {
        toast.success("Database restored successfully");
        setRestoreModalOpen(false);
        setRestoreConfirm("");
        setSelectedBackup(null);
        fetchStatus();
        fetchBackups();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to restore backup");
      }
    } catch (error) {
      toast.error("Failed to restore backup");
    } finally {
      setRestoringBackup(null);
    }
  };

  // Delete backup
  const handleDeleteBackup = async (backupId) => {
    if (!window.confirm("Are you sure you want to delete this backup?")) return;
    
    try {
      const response = await fetch(
        `${API}/admin/system-recovery/backups/${backupId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );
      
      if (response.ok) {
        toast.success("Backup deleted");
        fetchBackups();
        fetchStatus();
      } else {
        toast.error("Failed to delete backup");
      }
    } catch (error) {
      toast.error("Failed to delete backup");
    }
  };

  // Open maintenance modal
  const openMaintenanceModal = (action) => {
    setMaintenanceAction(action);
    setMaintenanceConfirm("");
    setMaintenanceMessage("");
    setMaintenanceModalOpen(true);
  };

  // Open restore modal
  const openRestoreModal = (backup) => {
    setSelectedBackup(backup);
    setRestoreConfirm("");
    setRestoreModalOpen(true);
  };

  // Get health badge
  const getHealthBadge = (health) => {
    switch (health) {
      case "healthy":
        return (
          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
            <CheckCircle size={14} className="mr-1" /> Healthy
          </Badge>
        );
      case "warning":
        return (
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
            <Warning size={14} className="mr-1" /> Warning
          </Badge>
        );
      case "failed":
        return (
          <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
            <X size={14} className="mr-1" /> Failed
          </Badge>
        );
      case "unconfigured":
        return (
          <Badge className="bg-zinc-500/20 text-zinc-400 border-zinc-500/30">
            <Warning size={14} className="mr-1" /> Unconfigured
          </Badge>
        );
      default:
        return (
          <Badge className="bg-zinc-500/20 text-zinc-400 border-zinc-500/30">
            <Database size={14} className="mr-1" /> No Backups
          </Badge>
        );
    }
  };

  if (loading) {
    return (
      <Card className="bg-zinc-800 border-zinc-700 mb-6">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <SpinnerGap size={24} className="animate-spin text-zinc-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const maintenanceEnabled = status?.maintenance_mode?.enabled;
  const backupHealth = status?.backup_health;

  return (
    <>
      <Card className="bg-gradient-to-br from-zinc-800 to-zinc-900 border-zinc-700 mb-6" data-testid="system-recovery-panel">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <ShieldCheck size={22} className="text-amber-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">System Recovery</CardTitle>
                <CardDescription className="text-zinc-400">
                  Platform backup, restore, and maintenance controls
                </CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Status Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Maintenance Mode Status */}
            <div className="bg-zinc-900/50 rounded-lg p-4 border border-zinc-700/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-zinc-400">Maintenance Mode</span>
                {maintenanceEnabled ? (
                  <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                    <Wrench size={12} className="mr-1" /> ON
                  </Badge>
                ) : (
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                    <CheckCircle size={12} className="mr-1" /> OFF
                  </Badge>
                )}
              </div>
              {maintenanceEnabled && status?.maintenance_mode?.enabled_at && (
                <p className="text-xs text-zinc-500">
                  Since {formatDistanceToNow(new Date(status.maintenance_mode.enabled_at), { addSuffix: true })}
                </p>
              )}
            </div>

            {/* Backup Health Status */}
            <div className="bg-zinc-900/50 rounded-lg p-4 border border-zinc-700/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-zinc-400">Backup Health</span>
                {getHealthBadge(backupHealth?.health)}
              </div>
              {backupHealth?.last_successful_backup && (
                <p className="text-xs text-zinc-500">
                  Last: {formatDistanceToNow(new Date(backupHealth.last_successful_backup), { addSuffix: true })}
                </p>
              )}
            </div>

            {/* Backup Count */}
            <div className="bg-zinc-900/50 rounded-lg p-4 border border-zinc-700/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-zinc-400">Stored Backups</span>
                <Badge variant="outline" className="border-zinc-600 text-zinc-300">
                  <HardDrive size={12} className="mr-1" />
                  {backupHealth?.backup_count || 0} / {backupHealth?.max_backups || 42}
                </Badge>
              </div>
              <p className="text-xs text-zinc-500">
                Auto-cleanup after {backupHealth?.max_backups || 42} backups
              </p>
            </div>
          </div>

          {/* Schedule Info */}
          <div className="bg-zinc-900/30 rounded-lg p-3 border border-zinc-700/30 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock size={16} className="text-zinc-500" />
              <div>
                <p className="text-xs text-zinc-300">
                  <span className="text-zinc-500">Schedule:</span> Every {backupHealth?.schedule_interval_hours || 4} hours
                  <span className="mx-2 text-zinc-600">|</span>
                  <span className="text-zinc-500">Retention:</span> {backupHealth?.max_backups || 42} backups
                </p>
                {backupHealth?.last_scheduled_backup && (
                  <p className="text-xs text-zinc-500 mt-0.5">
                    Last scheduled: {formatDistanceToNow(new Date(backupHealth.last_scheduled_backup), { addSuffix: true })}
                  </p>
                )}
              </div>
            </div>
            {backupHealth?.scheduled_overdue && (
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs">
                <Warning size={12} className="mr-1" /> Overdue
              </Badge>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 pt-2">
            <Button
              onClick={handleCreateBackup}
              disabled={creatingBackup}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="create-backup-btn"
            >
              {creatingBackup ? (
                <SpinnerGap size={16} className="mr-2 animate-spin" />
              ) : (
                <Plus size={16} className="mr-2" />
              )}
              Create Backup
            </Button>

            <Button
              onClick={() => openMaintenanceModal(maintenanceEnabled ? "disable" : "enable")}
              variant="outline"
              className={`border-zinc-600 ${
                maintenanceEnabled
                  ? "text-emerald-400 hover:bg-emerald-500/10"
                  : "text-amber-400 hover:bg-amber-500/10"
              }`}
              data-testid="toggle-maintenance-btn"
            >
              <Wrench size={16} className="mr-2" />
              {maintenanceEnabled ? "Disable Maintenance" : "Enable Maintenance"}
            </Button>
          </div>

          {/* Collapsible Backup List */}
          <Collapsible open={backupsOpen} onOpenChange={setBackupsOpen} className="pt-2">
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-between text-zinc-300 hover:bg-zinc-700/50 py-3"
                data-testid="toggle-backups-list"
              >
                <span className="flex items-center">
                  <Database size={16} className="mr-2" />
                  Available Backups ({backups.length})
                </span>
                {backupsOpen ? <CaretDown size={16} /> : <CaretRight size={16} />}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2">
              {backups.length === 0 ? (
                <div className="text-center py-6 text-zinc-500">
                  No backups available. Create one to get started.
                </div>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto pr-2">
                  {backups.map((backup) => (
                    <div
                      key={backup.backup_id}
                      className="bg-zinc-900/50 rounded-lg p-3 border border-zinc-700/50 flex items-center justify-between"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-white font-mono truncate">
                            {backup.backup_id}
                          </span>
                          {backup.status === "completed" ? (
                            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs">
                              Ready
                            </Badge>
                          ) : backup.status === "failed" ? (
                            <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs">
                              Failed
                            </Badge>
                          ) : (
                            <Badge className="bg-zinc-500/20 text-zinc-400 border-zinc-500/30 text-xs">
                              {backup.status}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-xs text-zinc-500">
                          <span className="flex items-center">
                            <Clock size={12} className="mr-1" />
                            {backup.created_at
                              ? format(new Date(backup.created_at), "MMM d, yyyy h:mm a")
                              : "Unknown"}
                          </span>
                          {backup.file_size_mb && (
                            <span className="flex items-center">
                              <HardDrive size={12} className="mr-1" />
                              {backup.file_size_mb} MB
                            </span>
                          )}
                        </div>
                        {backup.description && (
                          <p className="text-xs text-zinc-400 mt-1 truncate">{backup.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        {backup.status === "completed" && backup.file_exists && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openRestoreModal(backup)}
                            disabled={restoringBackup === backup.backup_id}
                            className="border-zinc-600 text-zinc-300 hover:bg-zinc-700"
                            data-testid={`restore-${backup.backup_id}`}
                          >
                            {restoringBackup === backup.backup_id ? (
                              <SpinnerGap size={14} className="animate-spin" />
                            ) : (
                              <ArrowCounterClockwise size={14} />
                            )}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteBackup(backup.backup_id)}
                          className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                          data-testid={`delete-${backup.backup_id}`}
                        >
                          <Trash size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>

      {/* Maintenance Mode Confirmation Modal */}
      <Dialog open={maintenanceModalOpen} onOpenChange={setMaintenanceModalOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Wrench size={20} className="text-amber-400" />
              {maintenanceAction === "enable" ? "Enable" : "Disable"} Maintenance Mode
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {maintenanceAction === "enable"
                ? "When enabled, non-admin API writes will be blocked. Users will see a maintenance message."
                : "Disabling maintenance mode will restore normal operation."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {maintenanceAction === "enable" && (
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">
                  Maintenance Message (optional)
                </label>
                <Input
                  value={maintenanceMessage}
                  onChange={(e) => setMaintenanceMessage(e.target.value)}
                  placeholder="System maintenance in progress..."
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
            )}

            <div>
              <label className="text-sm text-zinc-400 mb-2 block">
                Type <span className="font-mono text-amber-400">MAINTENANCE</span> to confirm
              </label>
              <Input
                value={maintenanceConfirm}
                onChange={(e) => setMaintenanceConfirm(e.target.value.toUpperCase())}
                placeholder="MAINTENANCE"
                className="bg-zinc-800 border-zinc-700 text-white font-mono"
                data-testid="maintenance-confirm-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setMaintenanceModalOpen(false)}
              className="border-zinc-600 text-zinc-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleMaintenanceToggle}
              disabled={maintenanceConfirm !== "MAINTENANCE" || togglingMaintenance}
              className={
                maintenanceAction === "enable"
                  ? "bg-amber-600 hover:bg-amber-700 text-white"
                  : "bg-emerald-600 hover:bg-emerald-700 text-white"
              }
              data-testid="confirm-maintenance-btn"
            >
              {togglingMaintenance && <SpinnerGap size={16} className="mr-2 animate-spin" />}
              {maintenanceAction === "enable" ? "Enable" : "Disable"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore Confirmation Modal */}
      <Dialog open={restoreModalOpen} onOpenChange={setRestoreModalOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <ArrowCounterClockwise size={20} className="text-red-400" />
              Restore Database
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              <span className="text-red-400 font-medium">Warning:</span> This will overwrite the current database
              with the selected backup. A safety backup will be created automatically before restore.
            </DialogDescription>
          </DialogHeader>

          {selectedBackup && (
            <div className="bg-zinc-800 rounded-lg p-3 border border-zinc-700">
              <div className="text-sm text-white font-mono mb-1">{selectedBackup.backup_id}</div>
              <div className="text-xs text-zinc-400">
                Created: {selectedBackup.created_at
                  ? format(new Date(selectedBackup.created_at), "MMM d, yyyy h:mm a")
                  : "Unknown"}
              </div>
              {selectedBackup.file_size_mb && (
                <div className="text-xs text-zinc-400">Size: {selectedBackup.file_size_mb} MB</div>
              )}
            </div>
          )}

          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm text-zinc-400 mb-2 block">
                Type <span className="font-mono text-red-400">RESTORE</span> to confirm
              </label>
              <Input
                value={restoreConfirm}
                onChange={(e) => setRestoreConfirm(e.target.value.toUpperCase())}
                placeholder="RESTORE"
                className="bg-zinc-800 border-zinc-700 text-white font-mono"
                data-testid="restore-confirm-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRestoreModalOpen(false)}
              className="border-zinc-600 text-zinc-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRestore}
              disabled={restoreConfirm !== "RESTORE" || restoringBackup}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-restore-btn"
            >
              {restoringBackup && <SpinnerGap size={16} className="mr-2 animate-spin" />}
              Restore Database
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
